import { useRef, useState } from 'react';
import { colour, radius, space, text } from '../../design/tokens.ts';
import { Button, Card, Field, Select, Tag, TextArea, TextInput } from '../../components/basics.tsx';
import { CouldNotLoad, Empty, Loading } from '../../components/states.tsx';
import { Dialog } from '../../components/Dialog.tsx';
import { api } from '../../api/client.ts';
import { useLoad } from '../../app/useLoad.ts';

/**
 * Cameras planned before there is a splat to put them in.
 *
 * A shoot is designed long before anything is captured, so this works in a
 * project with nothing in it at all. None of it spends a camera number: a
 * `vc_id` is fixed forever, so it is taken at the moment a camera is really
 * placed — "Build it" — and not a day earlier.
 */

export interface Sketch {
  key: string;
  content_type: string;
  bytes: number;
  caption?: string;
  /** Null when there is no media bucket, rather than a link that 404s. */
  url: string | null;
}

export interface Brief {
  _id: string;
  name: string;
  /** What the camera is for, as somebody wrote it. */
  says?: string;
  /** One line about where it stands, from the server. */
  standing: string;
  assigned_to?: string;
  sketches: Sketch[];
  became?: { vc_id: string; at: string };
  built: boolean;
}

interface Answer {
  briefs: Brief[];
  to_build: number;
  limits: { sketch_max_bytes: number; sketch_types: string[]; sketches_per_brief: number };
  can_upload: boolean;
}

/** A splat this project could build a camera into. */
export interface SplatChoice {
  splat_id: string;
  says: string;
  version: number;
}

async function sha256Of(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function Briefs({
  projectId,
  canPlan,
  splats,
  onBuilt
}: {
  projectId: string;
  canPlan: boolean;
  splats: SplatChoice[];
  onBuilt: () => void;
}) {
  const { state, reload } = useLoad<Answer>(
    () => api.get(`/api/projects/${encodeURIComponent(projectId)}/vc-briefs`),
    [projectId]
  );
  const [editing, setEditing] = useState<{
    id: string | null;
    name: string;
    about: string;
    assigned_to: string;
  } | null>(null);
  const [building, setBuilding] = useState<Brief | null>(null);
  const [splatForBuild, setSplatForBuild] = useState('');
  const [busy, setBusy] = useState(false);
  /** A line under the heading. `bad` so good news is not printed in red. */
  const [toast, setToast] = useState<{ says: string; bad: boolean } | null>(null);

  const answer = state.data;
  const briefs = answer?.briefs ?? [];

  const save = async (): Promise<void> => {
    if (!editing) return;
    setBusy(true);
    try {
      const body = {
        name: editing.name.trim(),
        says: editing.about.trim(),
        assigned_to: editing.assigned_to.trim()
      };
      if (editing.id) await api.patch(`/api/vc-briefs/${editing.id}`, body);
      else await api.post(`/api/projects/${encodeURIComponent(projectId)}/vc-briefs`, body);
      setEditing(null);
      setToast(null);
      reload();
    } catch (err) {
      setToast({ says: err instanceof Error ? err.message : 'That could not be saved.', bad: true });
    } finally {
      setBusy(false);
    }
  };

  const drop = async (brief: Brief): Promise<void> => {
    try {
      await api.del(`/api/vc-briefs/${brief._id}`);
      reload();
    } catch (err) {
      setToast({ says: err instanceof Error ? err.message : 'That could not be dropped.', bad: true });
    }
  };

  const build = async (): Promise<void> => {
    if (!building || !splatForBuild) return;
    setBusy(true);
    try {
      const made = await api.post<{ vc: { vc_id: string } }>(
        `/api/vc-briefs/${building._id}/build`,
        { splat_id: splatForBuild }
      );
      setBuilding(null);
      setToast({ says: `${building.name} is now ${made.vc.vc_id}.`, bad: false });
      reload();
      onBuilt();
    } catch (err) {
      setToast({ says: err instanceof Error ? err.message : 'That could not be built.', bad: true });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Straight to S3 with a link Maestro mints for one key. The bytes never go
   * through the server, and nothing is recorded until the upload has actually
   * finished — so a failed upload leaves no sketch claiming to exist.
   */
  const upload = async (brief: Brief, file: File): Promise<void> => {
    setBusy(true);
    try {
      const sha256 = await sha256Of(file);
      const asked = await api.post<{ url: string; key: string }>(
        `/api/vc-briefs/${brief._id}/sketches/upload-url`,
        { content_type: file.type, bytes: file.size, sha256 }
      );
      const put = await fetch(asked.url, {
        method: 'PUT',
        body: file,
        headers: { 'content-type': file.type }
      });
      if (!put.ok) throw new Error(`The upload was refused (${put.status}).`);
      await api.post(`/api/vc-briefs/${brief._id}/sketches`, {
        key: asked.key,
        content_type: file.type,
        bytes: file.size
      });
      reload();
    } catch (err) {
      setToast({ says: err instanceof Error ? err.message : 'That sketch could not be added.', bad: true });
    } finally {
      setBusy(false);
    }
  };

  const removeSketch = async (brief: Brief, key: string): Promise<void> => {
    try {
      await api.del(`/api/vc-briefs/${brief._id}/sketches?key=${encodeURIComponent(key)}`);
      reload();
    } catch (err) {
      setToast({ says: err instanceof Error ? err.message : 'That sketch could not be removed.', bad: true });
    }
  };

  return (
    <>
      <Card
        title="Planned cameras"
        aside={
          canPlan ? (
            <Button
              tone="primary"
              onClick={() => setEditing({ id: null, name: '', about: '', assigned_to: '' })}
            >
              Plan a camera
            </Button>
          ) : undefined
        }
      >
        <span style={{ ...text.small, color: colour.inkSoft }}>
          Cameras you intend to build, written down before there is a splat to put them in. Planning
          one spends no number — a camera takes its id when it is built.
        </span>
        {toast && (
          <span style={{ ...text.note, color: toast.bad ? colour.staleInk : colour.good }}>
            {toast.says}
          </span>
        )}
      </Card>

      {state.status === 'loading' && !answer && <Loading says="Loading the plan…" rows={3} />}
      {state.status === 'failed' && !answer && (
        <CouldNotLoad what="the planned cameras" error={state.error} onRetry={reload} />
      )}

      {answer && briefs.length === 0 && (
        <Empty
          title="No cameras planned yet"
          says="Describe the cameras you mean to build — what each one covers, who will place it, and a sketch if you have one. They wait here until a splat arrives."
          {...(canPlan
            ? {
                action: (
                  <Button
                    tone="primary"
                    onClick={() => setEditing({ id: null, name: '', about: '', assigned_to: '' })}
                  >
                    Plan a camera
                  </Button>
                )
              }
            : {})}
        />
      )}

      {briefs.map((brief) => (
        <BriefCard
          key={brief._id}
          brief={brief}
          canPlan={canPlan}
          canUpload={answer?.can_upload ?? false}
          splats={splats}
          busy={busy}
          onEdit={() =>
            setEditing({
              id: brief._id,
              name: brief.name,
              about: brief.says ?? '',
              assigned_to: brief.assigned_to ?? ''
            })
          }
          onDrop={() => void drop(brief)}
          onBuild={() => {
            setBuilding(brief);
            setSplatForBuild(splats[0]?.splat_id ?? '');
          }}
          onUpload={(file) => void upload(brief, file)}
          onRemoveSketch={(key) => void removeSketch(brief, key)}
        />
      ))}

      {editing && (
        <Dialog
          title={editing.id ? `Edit ${editing.name}` : 'Plan a camera'}
          says="A name you will recognise on the day, and enough about what it is for that somebody else could place it. No number is spent until it is built."
          confirm={editing.id ? 'Save' : 'Plan it'}
          confirmWhyNot={editing.name.trim() === '' ? 'Give it a name first.' : undefined}
          onConfirm={() => void save()}
          onClose={() => setEditing(null)}
          busy={busy}
        >
          <Field label="Name">
            <TextInput
              value={editing.name}
              onChange={(name) => setEditing({ ...editing, name })}
              placeholder="hero_wide"
            />
          </Field>
          <Field label="What it is for">
            <TextArea
              value={editing.about}
              onChange={(about) => setEditing({ ...editing, about })}
              placeholder="Chest height by the doorway, covers the table and the entrance."
              rows={4}
            />
          </Field>
          <Field label="Who will place it (optional)">
            <TextInput
              value={editing.assigned_to}
              onChange={(assigned_to) => setEditing({ ...editing, assigned_to })}
              placeholder="Kcross"
            />
          </Field>
        </Dialog>
      )}

      {building && (
        <Dialog
          title={`Build ${building.name}`}
          says="This is the moment a camera takes its number, and a number is fixed forever and never reused."
          confirm="Build it"
          confirmWhyNot={splatForBuild === '' ? 'Pick the splat it goes into.' : undefined}
          onConfirm={() => void build()}
          onClose={() => setBuilding(null)}
          busy={busy}
        >
          <Field label="Into which splat">
            <Select
              value={splatForBuild}
              onChange={setSplatForBuild}
              options={[
                { value: '', label: 'Pick a splat…' },
                ...splats.map((s) => ({
                  value: s.splat_id,
                  label: `${s.says} · version ${s.version}`
                }))
              ]}
            />
          </Field>
        </Dialog>
      )}
    </>
  );
}

function BriefCard({
  brief,
  canPlan,
  canUpload,
  splats,
  busy,
  onEdit,
  onDrop,
  onBuild,
  onUpload,
  onRemoveSketch
}: {
  brief: Brief;
  canPlan: boolean;
  canUpload: boolean;
  splats: SplatChoice[];
  busy: boolean;
  onEdit: () => void;
  onDrop: () => void;
  onBuild: () => void;
  onUpload: (file: File) => void;
  onRemoveSketch: (key: string) => void;
}) {
  const picker = useRef<HTMLInputElement>(null);

  return (
    <Card title={brief.name} aside={brief.built ? brief.became?.vc_id : undefined}>
      {/* Not a Status: "Not created yet" and the rest are what Maestro says
          about a VC's setup inside a splat, and a brief is not one of those
          yet. `standing` is the server's own sentence about it. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}>
        <Tag>{brief.standing}</Tag>
      </div>

      {brief.says && <span style={{ ...text.small, color: colour.inkSoft }}>{brief.says}</span>}

      {brief.sketches.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight }}>
          {brief.sketches.map((sketch) => (
            <figure
              key={sketch.key}
              style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 4, width: 160 }}
            >
              {sketch.url ? (
                <img
                  src={sketch.url}
                  alt={sketch.caption ?? `a sketch of ${brief.name}`}
                  style={{
                    width: 160,
                    height: 110,
                    objectFit: 'cover',
                    borderRadius: radius.control,
                    border: `1px solid ${colour.edge}`,
                    background: colour.surfaceSunk
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 160,
                    height: 110,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: radius.control,
                    border: `1px dashed ${colour.edge}`,
                    background: colour.surfaceSunk,
                    ...text.note,
                    color: colour.inkMuted,
                    textAlign: 'center',
                    padding: 8
                  }}
                >
                  No media bucket, so this cannot be shown
                </div>
              )}
              <figcaption style={{ ...text.note, color: colour.inkMuted }}>
                {sketch.caption ?? `${Math.round(sketch.bytes / 1024)}KB`}
              </figcaption>
              {canPlan && !brief.built && (
                <Button onClick={() => onRemoveSketch(sketch.key)}>Remove</Button>
              )}
            </figure>
          ))}
        </div>
      )}

      {canPlan && !brief.built && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}>
          <Button onClick={onEdit}>Edit</Button>
          <Button
            tone="primary"
            onClick={onBuild}
            disabled={splats.length === 0}
            disabledReason="There is no splat to put it in yet."
          >
            Build it
          </Button>
          <input
            ref={picker}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUpload(file);
              e.target.value = '';
            }}
          />
          <Button
            onClick={() => picker.current?.click()}
            disabled={!canUpload || busy}
            disabledReason={
              canUpload
                ? 'Working…'
                : 'This Maestro has no media bucket, so there is nowhere to put a sketch.'
            }
          >
            Add a sketch
          </Button>
          <Button tone="danger" onClick={onDrop}>
            Drop it
          </Button>
        </div>
      )}
    </Card>
  );
}
